import { createSlice, nanoid } from "@reduxjs/toolkit";

const createWorkInstruction = (title = "") => ({
  id: nanoid(),
  title,
  notes: "",
  revision: "",
  status: "DRAFT",
  description: "",
  create_date: new Date().toJSON(),
  update_date: new Date().toJSON(),
  items: [],
});

export const ITEM_OPTIONS = [
  "Instruction",
  "Pass/Fail",
  "Yes/No",
  "Task Item",
  "Sub-Section",
  "Custom",
];

const createTestPoint = (type) => {
  if (type === "TASK ITEM") {
    return {
      id: nanoid(),
      label: "",
      description: "",
      na: false,
      status: null,
      tested: 0,
      repeat: 1,
      checkbox_label: "Completed",
    };
  }
  if (type === "CUSTOM") {
    return {
      id: nanoid(),
      url: "",
      configuration: "",
      na: false,
      status: null,
      tested: 0,
      repeat: 1,
      checkbox_label: "Completed",
    };
  }
  if (type === "YES/NO") {
    return {
      id: nanoid(),
      label: "",
      description: "",
      test_types: "AFFIRMATIVE_YES",
      na: false,
      status: null,
      tested: 0,
      repeat: 1,
      checkbox_label: "",
    };
  }
  return {
    id: nanoid(),
    label: "",
    description: "",
    test_types: "AFFIRMATIVE_PASS",
    na: false,
    status: null,
    tested: 0,
    repeat: 1,
    checkbox_label: "Completed",
  };
};

const createItem = (name = "") => {
  const type = name.toLocaleUpperCase();
  const ITEM = { id: nanoid(), name, type, content: "", title: "" };
  switch (type) {
    case "INSTRUCTION":
      return {
        ...ITEM,
        infoPanel: "none",
      };
    case "PASS/FAIL":
      return {
        ...ITEM,
        label: "",
        description: "",
        test_points: [],
      };
    case "YES/NO":
      return {
        ...ITEM,
        label: "",
        description: "",
        test_points: [],
      };
    case "SUB-SECTION":
      return {
        ...ITEM,
        subName: name,
        subType: type,
        title: "",
      };
    case "TASK ITEM":
      return {
        ...ITEM,
        label: "",
        description: "",
        test_points: [],
      };
    case "CUSTOM":
      return {
        ...ITEM,
        label: "",
        description: "",
        test_points: [],
      };
    default:
      return ITEM;
  }
};

const createSection = (title = "") => ({
  id: nanoid(),
  title,
  items: [
    // createItem(ITEM_OPTIONS[0]), createItem(ITEM_OPTIONS[1])
  ],
});

const initialState = createWorkInstruction();

// console.log(JSON.stringify(initialState, null, 2));

export const workInstructionSlice = createSlice({
  name: "workInstruction",
  initialState,
  reducers: {
    setWorkInstruction: (state, action) => {
      // console.log(action.payload);
      state["id"] = action.payload.data.id;
      state["title"] = action.payload.data.title;
      state["notes"] = action.payload.data.notes;
      state["revision"] = action.payload.data.revision;
      state["status"] = action.payload.data.status;
      state["description"] = action.payload.data.description;
      state["create_date"] = action.payload.data.create_date;
      state["update_date"] = action.payload.data.update_date;
      state["items"] = action.payload.data.items;
    },
    clearWorkInstruction: (state, action) => {
      state["id"] = nanoid();
      state["title"] = "";
      state["notes"] = "";
      state["revision"] = "";
      state["status"] = "DRAFT";
      state["description"] = "";
      state["create_date"] = new Date().toJSON();
      state["update_date"] = new Date().toJSON();
      state["items"] = [];
    },
    handleWorkInstruction: (state, action) => {
      state[action.payload.name] = action.payload.value;
      // console.log(" state[action.payload.name]", state);
    },
    addSection: (state, action) => {
      const section = createSection(action.payload);
      state.items.push(section);
    },
    setSectionTitle: (state, action) => {
      const section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );
      section.title = action.payload.title;
    },
    updateSubSections: (state, action) => {
      state.items[action.payload.sectionIndex]["items"][
        action.payload.subSectionIndex
      ][action.payload.name] = action.payload.value;
    },
    removeSection: (state, action) => {
      state.items = state.items.filter(
        (section) => section.id !== action.payload.sectionId
      );
    },
    addSectionItem: (state, action) => {
      const item = createItem(action.payload.name);
      const section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );
      section.items.push(item);
    },
    setSectionItem: (state, action) => {
      const newItem = createItem(action.payload.name);
      // console.log(newItem);
      // const item = section.items.find((item) => item.id === action.payload.id);
      state.items[action.payload.sectionIndex]["items"][
        action.payload.subSectionIndex
      ] = {
        ...state.items[action.payload.sectionIndex]["items"][
          action.payload.subSectionIndex
        ],
        ...newItem,
      };
    },
    removeSectionItem: (state, action) => {
      const section = state.items.find(
        (section) => section.id === action.payload.sectionId
      );

      const subSection = section.items.find(
        (item) => item.id === action.payload.itemId
      );
      if (subSection.subType && subSection.subType !== subSection.type) {
        section["items"] = Array.from(section["items"]).map((element) => {
          if (element.id === action.payload.itemId) {
            return {
              id: element.id,
              name: element.subName,
              title: "",
              type: element.subType,
              subName: element.subName,
              subType: element.subType,
            };
          } else return element;
        });

        return;
      }

      section.items = section.items.filter(
        (item) => item.id !== action.payload.itemId
      );
    },
    addTestPoint: (state, action) => {
      state.items[action.payload.sectionIndex]["items"][
        action.payload.subSectionIndex
      ]["test_points"].push(createTestPoint(action.payload.type));
    },
    deleteTestPoint: (state, action) => {
      state.items[action.payload.sectionIndex]["items"][
        action.payload.subSectionIndex
      ]["test_points"] = Array.from(
        state.items[action.payload.sectionIndex]["items"][
          action.payload.subSectionIndex
        ]["test_points"]
      ).filter((item) => item.id !== action.payload.id);
    },
    updateTestPoint: (state, action) => {
      state.items[action.payload.sectionIndex]["items"][
        action.payload.subSectionIndex
      ]["test_points"] = Array.from(
        state.items[action.payload.sectionIndex]["items"][
          action.payload.subSectionIndex
        ]["test_points"]
      ).map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            [action.payload.testPointName]: action.payload.value,
          };
        } else return item;
      });
    },
  },
});
